/**
 * Fuel barge (GDD §3.6: orange pontoon, two white tanks with orange bands, railings, "FUEL" deck marking, four
 * blinking amber beacons; 23 × 11.5 m). Ported verbatim (concept L487–L506).
 */
import { BoxGeometry, CylinderGeometry, Group, Mesh, PlaneGeometry, SphereGeometry, type Material } from 'three';
import { mesh } from '../../world/geometry.ts';
import type { Materials } from '../materials.ts';

export interface BargeParts {
  beacons: Mesh[];
  label: Mesh;
}

export function buildBarge(M: Materials, labelMat: Material): Group & { userData: BargeParts } {
  const g = new Group();
  mesh(new BoxGeometry(11.5, 2.0, 23), M.orange, 0, 0.25, 0, g);
  mesh(new BoxGeometry(11.7, 0.4, 23.2), M.white, 0, 1.25, 0, g);
  mesh(new BoxGeometry(11.0, 0.2, 22.4), M.deck, 0, 1.52, 0, g);
  for (const s of [1, -1]) {
    const t = new CylinderGeometry(2.3, 2.3, 14, 32);
    t.rotateX(Math.PI / 2);
    mesh(t, M.tank, s * 2.65, 4.05, 2.2, g);
    for (const e of [-7, 7]) {
      const cap = new SphereGeometry(2.3, 24, 14);
      cap.scale(1, 1, 0.42);
      mesh(cap, M.tank, s * 2.65, 4.05, 2.2 + e, g);
    }
    for (const z of [-3.5, 2.2, 7.9]) {
      const bd = new CylinderGeometry(2.36, 2.36, 0.85, 32);
      bd.rotateX(Math.PI / 2);
      mesh(bd, M.orange, s * 2.65, 4.05, z, g);
    }
    for (const z of [-2, 2.2, 6.4]) mesh(new BoxGeometry(1.6, 1.6, 0.5), M.dark, s * 2.65, 2.2, z, g);
    for (let k = 0; k < 12; k++)
      mesh(new BoxGeometry(0.1, 1.0, 0.1), M.white, s * 5.55, 2.1, -10.8 + k * 1.95, g, false);
    mesh(new BoxGeometry(0.08, 0.08, 22.6), M.white, s * 5.55, 2.6, 0, g, false);
  }
  const pipe = new CylinderGeometry(0.22, 0.22, 13, 10);
  pipe.rotateX(Math.PI / 2);
  mesh(pipe, M.dark, 0, 6.2, 2.2, g);
  const lab = new Mesh(new PlaneGeometry(8.4, 4.2), labelMat);
  lab.rotation.x = -Math.PI / 2;
  lab.position.set(0, 1.64, -8.6);
  g.add(lab);
  const beacons = (
    [
      [-5.4, -11.2],
      [5.4, -11.2],
      [-5.4, 11.2],
      [5.4, 11.2],
    ] as const
  ).map((p) => mesh(new SphereGeometry(0.28, 10, 8), M.beacon, p[0], 2.1, p[1], g, false));
  g.userData = { beacons, label: lab } satisfies BargeParts;
  return g as Group & { userData: BargeParts };
}
